import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

const BACKEND_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:5001'
  : '';

export default function PublicArtistOnboardingView() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');
  const urlError = searchParams.get('error');

  const [loading, setLoading] = useState(true);
  const [requestData, setRequestData] = useState(null);
  const [error, setError] = useState(urlError ? decodeURIComponent(urlError) : null);

  const [isConnecting, setIsConnecting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(searchParams.get('success') === 'true');

  // Live Meta Page session state
  const [oauthSession, setOauthSession] = useState(null);
  const [selectedPageId, setSelectedPageId] = useState('');
  const [completingConnect, setCompletingConnect] = useState(false);

  // Simulation modal
  const [showSimModal, setShowSimModal] = useState(false);
  const [simPageName, setSimPageName] = useState('');

  // 1. Load initial request data
  useEffect(() => {
    if (!slug) return;
    fetch(`${BACKEND_URL}/api/meta-access/requests/${slug}`)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setRequestData(data.request);
          setSimPageName(`${data.request.clientName} Official`);
          if (data.request.status === 'COMPLETED' && !sessionId) {
            setIsSuccess(true);
          }
        } else {
          setError(data.error || 'Access link not found or expired');
        }
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [slug, sessionId]);

  // 2. Load OAuth session assets if returned from Facebook login
  useEffect(() => {
    if (sessionId) {
      setLoading(true);
      fetch(`${BACKEND_URL}/api/meta-access/oauth-session/${sessionId}`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setOauthSession(data.session);
            if (data.session.pages && data.session.pages.length > 0) {
              setSelectedPageId(data.session.pages[0].id);
            }
          } else {
            setError(data.error || 'OAuth session expired. Please restart authorization.');
          }
        })
        .catch(err => setError(err.message))
        .finally(() => setLoading(false));
    }
  }, [sessionId]);

  const handleStartConnect = async () => {
    setIsConnecting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/meta-access/auth-url?slug=${slug}`);
      const data = await res.json();
      if (data.isMockEnabled) {
        setIsConnecting(false);
        setShowSimModal(true);
      } else if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      alert('Error initiating Meta connection: ' + err.message);
      setIsConnecting(false);
    }
  };

  const handleConfirmAssetSelection = async (e) => {
    e.preventDefault();
    if (!selectedPageId) {
      alert('Please select a Facebook Page');
      return;
    }
    setCompletingConnect(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/meta-access/complete-connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          slug,
          selectedPageId
        })
      });
      const data = await res.json();
      if (data.success) {
        setOauthSession(null);
        setIsSuccess(true);
      } else {
        alert(data.error || 'Failed to complete Meta asset connection');
      }
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setCompletingConnect(false);
    }
  };

  const handleConfirmSimulation = async () => {
    setIsConnecting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/meta-access/mock-connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug,
          artistPageName: simPageName
        })
      });
      const data = await res.json();
      if (data.success) {
        setShowSimModal(false);
        setIsSuccess(true);
      } else {
        alert(data.error || 'Failed to connect');
      }
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setIsConnecting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8f7fa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, sans-serif' }}>
        <div style={{ textAlign: 'center', color: '#5b6275' }}>
          <div style={{ fontSize: '28px', marginBottom: '8px' }}>⏳</div>
          <div style={{ fontWeight: '600', fontSize: '14px' }}>Loading authorization portal...</div>
        </div>
      </div>
    );
  }

  if (error && !oauthSession) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8f7fa', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: 'system-ui, sans-serif' }}>
        <div style={{ backgroundColor: '#ffffff', borderRadius: '24px', padding: '40px', maxWidth: '440px', width: '100%', textAlign: 'center', border: '1px solid rgba(15, 9, 28, 0.08)', boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>⚠️</div>
          <h2 style={{ fontSize: '18px', fontWeight: '800', margin: '0 0 8px 0', color: '#1f2937' }}>Authorization Notice</h2>
          <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 20px 0' }}>{error}</p>
          <button
            onClick={() => {
              setError(null);
              window.location.href = `/r/${slug}`;
            }}
            style={{ padding: '10px 20px', backgroundColor: '#0064E0', color: '#fff', border: 'none', borderRadius: '12px', fontWeight: '700', cursor: 'pointer' }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const agency = requestData?.agency || { name: 'Louder Operations' };
  const clientName = requestData?.clientName || 'Artist Team';
  const customMessage = requestData?.customMessage;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f7fa', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '40px 20px', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <div style={{ maxWidth: '520px', width: '100%', margin: '0 auto' }}>
        {/* Louder / Agency Brand */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: '#ffffff',
            padding: '6px 14px',
            borderRadius: '20px',
            border: '1px solid rgba(15, 9, 28, 0.08)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
          }}>
            <span style={{ fontWeight: '800', fontSize: '12px', background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)', color: '#ffffff', padding: '2px 6px', borderRadius: '6px' }}>
              LOUDER
            </span>
            <span style={{ fontSize: '12px', fontWeight: '700', color: '#1f2937' }}>{agency.name}</span>
            <span style={{ fontSize: '10px', color: '#10b981', fontWeight: '700', backgroundColor: 'rgba(16, 185, 129, 0.1)', padding: '2px 6px', borderRadius: '10px' }}>
              ✓ Verified Partner
            </span>
          </div>
        </div>

        {/* Main Card */}
        <div style={{ backgroundColor: '#ffffff', borderRadius: '28px', border: '1px solid rgba(15, 9, 28, 0.08)', padding: '36px', boxShadow: '0 20px 50px rgba(15, 9, 28, 0.06)' }}>
          
          {/* STATE 1: ASSET SELECTION (LIVE META RETURN) */}
          {oauthSession ? (
            <form onSubmit={handleConfirmAssetSelection}>
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: 'rgba(0, 100, 224, 0.1)', color: '#0064E0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', margin: '0 auto 12px auto' }}>
                  📘
                </div>
                <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#1f2937', margin: '0 0 6px 0' }}>
                  Select Artist Facebook Page
                </h2>
                <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
                  Logged in as <strong style={{ color: '#1f2937' }}>{oauthSession.metaUserName}</strong>
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
                {/* Select Page */}
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#374151', marginBottom: '6px' }}>
                    Choose Page to Authorize:
                  </label>
                  {oauthSession.pages && oauthSession.pages.length > 0 ? (
                    <select
                      value={selectedPageId}
                      onChange={e => setSelectedPageId(e.target.value)}
                      style={{ width: '100%', padding: '12px 14px', borderRadius: '12px', border: '1px solid #d1d5db', fontSize: '13px', backgroundColor: '#fff', boxSizing: 'border-box' }}
                    >
                      {oauthSession.pages.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.name} {p.instagramUsername ? `(📸 @${p.instagramUsername})` : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div style={{ padding: '12px 14px', borderRadius: '12px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', fontSize: '12px', color: '#b91c1c', lineHeight: '1.5' }}>
                      <strong>No Facebook Pages detected.</strong>
                      <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#991b1b' }}>
                        If you manage this Page, make sure you checked the box next to your Page during the Facebook Login prompt, or that your Business Portfolio grants access.
                      </p>
                    </div>
                  )}
                </div>

                {/* Detected Instagram Info */}
                {(() => {
                  const currPage = (oauthSession.pages || []).find(p => p.id === selectedPageId) || oauthSession.pages?.[0];
                  if (currPage?.instagramUsername) {
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', borderRadius: '12px', backgroundColor: '#fdf2f8', border: '1px solid #fbcfe8', fontSize: '12px', color: '#9d174d' }}>
                        <span>📸</span>
                        <span>Connected Instagram: <strong>@{currPage.instagramUsername}</strong></span>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              <button
                type="submit"
                disabled={completingConnect || !selectedPageId}
                style={{
                  width: '100%',
                  padding: '16px',
                  backgroundColor: '#0064E0',
                  color: '#ffffff',
                  borderRadius: '16px',
                  border: 'none',
                  fontWeight: '800',
                  fontSize: '14px',
                  cursor: 'pointer',
                  boxShadow: '0 6px 20px rgba(0, 100, 224, 0.3)',
                  transition: 'all 0.2s ease'
                }}
              >
                {completingConnect ? '⚡ Linking Page with Louder...' : '✓ Confirm & Grant Page Access'}
              </button>
            </form>
          ) : isSuccess ? (
            /* STATE 2: SUCCESS VIEW */
            <div style={{ textAlign: 'center', padding: '10px 0' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px', margin: '0 auto 16px auto' }}>
                ✓
              </div>
              <h2 style={{ fontSize: '22px', fontWeight: '800', color: '#1f2937', margin: '0 0 8px 0' }}>
                Access Granted Successfully! 🎉
              </h2>
              <p style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.6', margin: '0 0 24px 0' }}>
                Thank you! Your Facebook Page & connected Instagram have been securely linked with <strong>{agency.name}</strong>.
              </p>

              <div style={{ backgroundColor: '#f9fafb', borderRadius: '16px', padding: '16px', textAlign: 'left', fontSize: '13px', border: '1px solid rgba(15, 9, 28, 0.06)', marginBottom: '20px' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '8px' }}>Connected Assets</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span>Facebook Page Ads & Audiences</span>
                  <span style={{ color: '#10b981', fontWeight: '700' }}>✓ Connected</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span>Connected Instagram Account</span>
                  <span style={{ color: '#10b981', fontWeight: '700' }}>✓ Linked</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Lead Form Sync & Webhooks</span>
                  <span style={{ color: '#10b981', fontWeight: '700' }}>✓ Active</span>
                </div>
              </div>

              <p style={{ fontSize: '11px', color: '#9ca3af', margin: 0 }}>
                You can safely close this browser window.
              </p>
            </div>
          ) : (
            /* STATE 3: INITIAL ONBOARDING REQUEST */
            <div>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#1f2937', margin: '0 0 8px 0', letterSpacing: '-0.02em' }}>
                  Grant Page Access for <br />
                  <span style={{ color: '#0064E0' }}>{clientName}</span>
                </h1>
                <p style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.6', margin: 0 }}>
                  {customMessage || `Grant ${agency.name} partner access to your Facebook Page & Instagram to run campaigns, build custom audiences, and sync fan leads.`}
                </p>
              </div>

              {/* Permissions List */}
              <div style={{ backgroundColor: 'rgba(15, 9, 28, 0.02)', borderRadius: '18px', padding: '18px', border: '1px solid rgba(15, 9, 28, 0.06)', marginBottom: '24px' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', marginBottom: '12px' }}>
                  Requested Access
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    <span style={{ fontSize: '18px' }}>📘</span>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937' }}>Facebook Page & Audiences</div>
                      <div style={{ fontSize: '11px', color: '#6b7280' }}>Create ads on behalf of page & build custom audiences from fan engagement</div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    <span style={{ fontSize: '18px' }}>📸</span>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937' }}>Connected Instagram Account</div>
                      <div style={{ fontSize: '11px', color: '#6b7280' }}>Deliver ads across Instagram Reels, Feeds, and Stories</div>
                    </div>
                  </div>

                  {requestData?.reqLeads && (
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      <span style={{ fontSize: '18px' }}>👥</span>
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937' }}>Lead Generation & Presale Forms</div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>Instant real-time sync for tour presale signups and fan lists</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* 1-Click Meta Button */}
              <button
                onClick={handleStartConnect}
                disabled={isConnecting}
                style={{
                  width: '100%',
                  padding: '16px',
                  backgroundColor: '#0064E0',
                  color: '#ffffff',
                  borderRadius: '16px',
                  border: 'none',
                  fontWeight: '800',
                  fontSize: '15px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  boxShadow: '0 6px 20px rgba(0, 100, 224, 0.3)',
                  marginBottom: '16px',
                  transition: 'all 0.2s ease'
                }}
              >
                <span>📘</span>
                <span>{isConnecting ? 'Connecting to Meta...' : 'Grant Access via Meta'}</span>
                <span>➔</span>
              </button>

              {/* Privacy Reassurance */}
              <div style={{ borderTop: '1px solid rgba(15, 9, 28, 0.08)', paddingTop: '16px', fontSize: '11px', color: '#6b7280', lineHeight: '1.6' }}>
                <div style={{ fontWeight: '700', color: '#374151', marginBottom: '4px' }}>🔒 Security & Privacy Guarantees:</div>
                • We never see or ask for your personal Facebook password.<br />
                • We cannot access your personal profile, photos, or private messages.<br />
                • Access can be revoked anytime in your Page Settings.
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '11px', color: '#9ca3af' }}>
          Powered by Louder Tools • Meta Business Partner
        </div>
      </div>

      {/* Simulated Connect Modal (for Demo Mode when live API keys are not set) */}
      {showSimModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ backgroundColor: '#ffffff', borderRadius: '24px', maxWidth: '440px', width: '100%', padding: '28px', boxShadow: '0 20px 50px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <span style={{ fontSize: '24px' }}>📘</span>
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: '800', margin: 0 }}>Meta Business Login (Simulated)</h3>
                <span style={{ fontSize: '11px', color: '#6b7280' }}>Select assets to grant to {agency.name}</span>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#374151', marginBottom: '4px' }}>Facebook Page to Link:</label>
                <input
                  type="text"
                  value={simPageName}
                  onChange={e => setSimPageName(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '12px', boxSizing: 'border-box' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setShowSimModal(false)} style={{ padding: '8px 14px', borderRadius: '10px', border: 'none', backgroundColor: '#f3f4f6', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                Cancel
              </button>
              <button onClick={handleConfirmSimulation} disabled={isConnecting} style={{ padding: '8px 16px', borderRadius: '10px', border: 'none', backgroundColor: '#0064E0', color: '#ffffff', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>
                {isConnecting ? 'Connecting...' : 'Confirm & Grant Access'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
